/**
 * Route definition for /item/:chain/:contract/:tokenId/
 *
 * Uses TanStack Router's loader to prefetch item details server-side.
 * Fetches collection information and NFT metadata before render.
 * The chain parameter is captured but not used for data fetching (could be used for validation).
 *
 * @module routes/item/$chain/$tokenId
 * @category Routes
 */

import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'react-toastify'
import { getCollectionById, getNFTMetadata, ApiError } from '@/lib/api'
import { createNftProductSchema } from '@/utils/metadata'
import { setPageMetadata } from '@/utils/page-metadata'
import { ipfs2md5 } from '@/utils'
import ItemDetailsContainer from '@/containers/ItemDetails'

/**
 * Loader function that runs before the component renders.
 *
 * Server-side: fetches collection and NFT metadata, returns them
 * Client-side: provides cached data if available, refetches if stale
 *
 * @param {Object} params - Route parameters
 * @param {string} params.chain - The blockchain chain identifier
 * @param {string} params.contract - The collection contract address or database ID
 * @param {string} params.tokenId - The token ID of the NFT
 * @returns {Promise<{initialCollectionInfo: any, initialTokenData: any, chain: string, contract: string, tokenId: string}>}
 */
export const Route = createFileRoute('/$locale/item/$chain/$contract/$tokenId/')({
  /**
   * Preloads the item data before component mount.
   * Supports SSR - data is fetched on server during initial render
   * and hydrated on client for seamless transition.
   */
  loader: async ({ params: { chain, contract, tokenId } }) => {
    // Validate params exist
    if (!contract || !tokenId) {
      throw new Error('Contract and token ID are required')
    }

    try {
      // Fetch collection information using typed API
      const collectionInfo = await getCollectionById(contract)

      // Fetch NFT metadata using typed API
      const tokenMetadata = await getNFTMetadata(contract, tokenId)

      if (!tokenMetadata) {
        throw new Error('NFT metadata not found')
      }

      // Return data to be available via Route.useLoaderData()
      return {
        initialCollectionInfo: collectionInfo,
        initialTokenData: tokenMetadata,
        chain,
        contract,
        tokenId,
      }
    } catch (error) {
      // Log error, show toast, and propagate to error boundary
      console.error('Failed to load item details:', error)
      toast.error('Failed to load item details. Please try again.')
      throw error
    }
  },

  /**
   * The component that renders the item details page.
   * Receives loader data via Route.useLoaderData() hook.
   */
  component: ItemPage,
  // SSR is enabled - this route will render on server with metadata captured.
  // Web3-dependent components are deferred to client via window check in ItemPage.
})

/**
 * Item details page component.
 *
 * Receives preloaded item data from the loader and passes it to
 * the ItemDetailsContainer. This eliminates the need for useEffect-based
 * data fetching and provides instant rendering.
 *
 * Uses dynamic import for ItemDetailsContainer to avoid SSR issues with Web3.
 *
 * @returns {React.ReactElement}
 */
function ItemPage() {
  // Get the preloaded data from the route loader
  const { initialCollectionInfo, initialTokenData, chain, contract, tokenId } = Route.useLoaderData() as {
    initialCollectionInfo: any;
    initialTokenData: any;
    chain: string;
    contract: string;
    tokenId: string;
  }

  // Build full URL for the NFT
  const pageUrl = `https://stealthisnft.xyz/item/${chain}/${contract}/${tokenId}`

  // Parse the token metadata (stringified JSON) to extract rich data
  let parsedMetadata: any = null;
  if (initialTokenData && initialTokenData.metadata) {
    try {
      parsedMetadata = typeof initialTokenData.metadata === 'string'
        ? JSON.parse(initialTokenData.metadata)
        : initialTokenData.metadata;
    } catch (e) {
      console.warn('Failed to parse token metadata:', e);
    }
  }

  // Safely access token data with fallbacks
  const tokenData = initialTokenData || {};

  // Image selection - prioritize metadata.image (the actual NFT image)
  // Fallback to direct fields, then CardImg's logic (ipfs2md5 conversion)
  const rawImage = parsedMetadata?.image ||
                   tokenData.img_ipfs ||
                   tokenData.image ||
                   tokenData.image_url ||
                   tokenData.image_ipfs ||
                   tokenData.image_cached ||
                   initialCollectionInfo?.image ||
                   ''

  // Convert IPFS to CDN URL if needed (matches CardImg behavior)
  // Append base64-encoded image URL as query param for cache busting/fallback
  const nftImage = rawImage ? ipfs2md5(rawImage, true) + '.jpg?' + (typeof btoa !== 'undefined' ? btoa(rawImage) : Buffer.from(rawImage).toString('base64')) : undefined

  const nftTitle = tokenData.title || parsedMetadata?.name || `NFT #${tokenId}`
  const collectionName = initialCollectionInfo?.title || initialCollectionInfo?.name || 'Unknown Collection'

  // Description: prefer metadata.description, then fallback to tokenData description, then default
  const nftDescription = parsedMetadata?.description ||
                         tokenData.description ||
                         `View ${nftTitle} from ${collectionName}`

  // Extract attributes from parsed metadata
  let tokenAttributes: Array<{ trait_type: string; value: string | number }> = [];
  if (parsedMetadata?.attributes && Array.isArray(parsedMetadata.attributes)) {
    tokenAttributes = parsedMetadata.attributes.map((attr: any) => ({
      trait_type: attr.trait_type || attr.key || 'Unknown',
      value: attr.value || attr.value || ''
    }));
  }

  // Generate structured data for the NFT product (JSON-LD)
  const structuredData = createNftProductSchema({
    name: nftTitle,
    tokenId: tokenId,
    collectionName: collectionName,
    url: pageUrl,
    image: nftImage || `https://stealthisnft.xyz/og-image.png`, // Fallback to generic og image
    description: nftDescription,
    price: initialTokenData?.price || '',
    currency: 'USD',
    chain: chain,
    contractAddress: contract,
    creator: initialTokenData?.creator || initialCollectionInfo?.creator,
    owner: initialTokenData?.owner,
    attributes: tokenAttributes,
  })

  // Set dynamic metadata including JSON-LD
  setPageMetadata({
    title: `${collectionName} - ${nftTitle} | VIRI`,
    description: nftDescription,
    ogImage: nftImage || undefined,
    ogType: 'product',
    twitterCard: 'summary_large_image',
    jsonLd: structuredData,
  })

  // Render the ItemDetailsContainer.
  // This route is SSR-enabled. The useItemDetails hook and child components
  // must be safe for SSR (deferring Web3 calls to useEffect).
  return (
    <ItemDetailsContainer
      chain={chain}
      contract={contract}
      tokenId={tokenId}
      initialCollectionInfo={initialCollectionInfo}
      initialTokenData={initialTokenData}
    />
  )
}
